import { DeleteIcon, MoreHorizontalIcon, TagsIcon } from '@blocksuite/icons/rc';
import type { MenuProps } from '@pique/component';
import { IconButton, Input, Menu, Scrollable } from '@pique/component';
import { useNavigateHelper } from '@pique/core/hooks/use-navigate-helper';
import { WorkspaceLegacyProperties } from '@pique/core/modules/properties';
import type { Tag } from '@pique/core/modules/tag';
import { DeleteTagConfirmModal, TagService } from '@pique/core/modules/tag';
import { useI18n } from '@pique/i18n';
import { useLiveData, useService } from '@toeverything/infra';
import clsx from 'clsx';
import { clamp } from 'lodash-es';
import type { HTMLAttributes, PropsWithChildren } from 'react';
import { useCallback, useMemo, useReducer, useRef, useState } from 'react';

import { TagItem, TempTagItem } from '../../page-list';
import type { MenuItemOption } from './menu-items';
import { renderMenuItemOptions } from './menu-items';
import * as styles from './tags-inline-editor.css';

interface TagsEditorProps {
  pageId: string;
  readonly?: boolean;
  focusedIndex?: number;
}

interface InlineTagsListProps
  extends Omit<HTMLAttributes<HTMLDivElement>, 'onChange'>,
  Omit<TagsEditorProps, 'onOptionsChange'> {
  onRemove?: () => void;
}

export const InlineTagsList = ({
  pageId,
  readonly,
  children,
  focusedIndex,
  onRemove,
}: PropsWithChildren<InlineTagsListProps>) => {
  const tagList = useService(TagService).tagList;
  const tags = useLiveData(tagList.tags$);
  const tagIds = useLiveData(tagList.tagIdsByPageId$(pageId));

  return (
    <div className={styles.inlineTagsContainer} data-testid="inline-tags-list">
      {tagIds.map((tagId: string, idx: number) => {
        const tag = tags.find((t: Tag) => t.id === tagId);
        if (!tag) {
          return null;
        }
        const onRemoved = readonly
          ? undefined
          : () => {
            tag.untag(pageId);
            onRemove?.();
          };
        return (
          <TagItem
            key={tagId}
            idx={idx}
            focused={focusedIndex === idx ? true : undefined}
            onRemoved={onRemoved}
            mode="inline"
            tag={tag}
          />
        );
      })}
      {children}
    </div>
  );
};

export const EditTagMenu = ({
  tagId,
  onTagDelete,
  children,
}: PropsWithChildren<{
  tagId: string;
  onTagDelete: (tagIds: string[]) => void;
}>) => {
  const t = useI18n();
  const legacyProperties = useService(WorkspaceLegacyProperties);
  const tagService = useService(TagService);
  const tagList = tagService.tagList;
  const tag = useLiveData(tagList.tagByTagId$(tagId));
  const tagColor = useLiveData(tag?.color$);
  const tagValue = useLiveData(tag?.value$);
  const navigate = useNavigateHelper();

  const menuProps = useMemo(() => {
    const options: MenuItemOption[] = [];
    const updateTagName = (name: string) => {
      if (name.trim() === '') {
        return;
      }
      tag?.rename(name);
    };
    options.push(
      <Input
        defaultValue={tagValue}
        onBlur={(e: FocusEvent & { currentTarget: HTMLInputElement }) => {
          updateTagName(e.currentTarget.value);
        }}
        onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            updateTagName(e.currentTarget.value);
          }
          e.stopPropagation();
        }}
        placeholder={t['Untitled']()}
      />
    );

    options.push('-');

    options.push({
      text: t['Delete'](),
      icon: <DeleteIcon />,
      type: 'danger',
      onClick() {
        onTagDelete([tag?.id || '']);
      },
    });

    options.push({
      text: t['com.pique.page-properties.tags.open-tags-page'](),
      icon: <TagsIcon />,
      onClick() {
        navigate.jumpToTag(legacyProperties.workspaceId, tag?.id || '');
      },
    });

    options.push('-');

    options.push(
      tagService.tagColors.map((item: readonly [string, string], i: number) => {
        const name = item[0];
        const color = item[1];
        return {
          text: name,
          icon: (
            <div key={i} className={styles.tagColorIconWrapper}>
              <div
                className={styles.tagColorIcon}
                style={{
                  backgroundColor: color,
                }}
              />
            </div>
          ),
          checked: tagColor === color,
          onClick() {
            tag?.changeColor(color);
          },
        };
      })
    );
    const items = renderMenuItemOptions(options);

    return {
      contentOptions: {
        onClick(e: React.MouseEvent) {
          e.stopPropagation();
        },
      },
      items,
    } satisfies Partial<MenuProps>;
  }, [
    legacyProperties.workspaceId,
    navigate,
    onTagDelete,
    t,
    tag,
    tagColor,
    tagService.tagColors,
    tagValue,
  ]);

  return <Menu {...menuProps}>{children}</Menu>;
};

type TagOption = Tag | { readonly create: true; readonly value: string };
const isCreateNewTag = (
  tagOption: TagOption
): tagOption is { readonly create: true; readonly value: string } => {
  return 'create' in tagOption;
};

export const TagsEditor = ({ pageId, readonly }: TagsEditorProps) => {
  const t = useI18n();
  const tagService = useService(TagService);
  const tagList = tagService.tagList;
  const tags = useLiveData(tagList.tags$);
  const tagIds = useLiveData(tagList.tagIdsByPageId$(pageId));
  const [inputValue, setInputValue] = useState('');
  const filteredTags = useLiveData(
    inputValue ? tagList.filterTagsByName$(inputValue) : tagList.tags$
  );
  const [open, setOpen] = useState(false);
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);

  const exactMatch = filteredTags.find((tag: Tag) => tag.value$.value === inputValue);
  const showCreateTag = !exactMatch && inputValue.trim();

  // tag option candidates to show in the tag dropdown
  const tagOptions: TagOption[] = useMemo(() => {
    if (showCreateTag) {
      return [{ create: true, value: inputValue } as const, ...filteredTags];
    } else {
      return filteredTags;
    }
  }, [filteredTags, inputValue, showCreateTag]);

  const [focusedIndex, setFocusedIndex] = useState<number>(-1);
  const [focusedInlineIndex, setFocusedInlineIndex] = useState<number>(
    tagIds.length
  );

  // -1: no focus
  const safeFocusedIndex = clamp(focusedIndex, -1, tagOptions.length - 1);
  // inline tags focus index can go beyond the length of tagIds
  // using -1 and tagIds.length to make keyboard navigation easier
  const safeInlineFocusedIndex = clamp(focusedInlineIndex, -1, tagIds.length);

  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const handleCloseModal = useCallback(
    (open: boolean) => {
      setOpen(open);
      setSelectedTagIds([]);
    },
    [setOpen]
  );

  const onTagDelete = useCallback(
    (tagIds: string[]) => {
      setOpen(true);
      setSelectedTagIds(tagIds);
    },
    [setOpen, setSelectedTagIds]
  );

  const onInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setInputValue(e.target.value);
    },
    []
  );

  const onToggleTag = useCallback(
    (id: string) => {
      const tagEntity = tagList.tags$.value.find((o: Tag) => o.id === id);
      if (!tagEntity) {
        return;
      }
      const isAdding = !tagIds.includes(id);
      if (isAdding) {
        // Enforce maximum of two tags per page
        if (tagIds.length >= 2) {
          return;
        }
        tagEntity.tag(pageId);
      } else {
        tagEntity.untag(pageId);
      }
    },
    [pageId, tagIds, tagList.tags$.value]
  );

  const focusInput = useCallback(() => {
    inputRef.current?.focus();
  }, []);

  const [nextColor, rotateNextColor] = useReducer(
    (color: string) => {
      const idx = tagService.tagColors.findIndex((c: readonly [string, string]) => c[1] === color);
      const nextColorEntry = tagService.tagColors[(idx + 1) % tagService.tagColors.length];
      return nextColorEntry?.[1] ?? color;
    },
    tagService.tagColors[
    Math.floor(Math.random() * tagService.tagColors.length)
    ]?.[1] ?? '#000000'
  );

  const onCreateTag = useCallback(
    (name: string) => {
      rotateNextColor();
      const newTag = tagList.createTag(name.trim(), nextColor);
      return newTag.id;
    },
    [nextColor, tagList]
  );

  const onSelectTagOption = useCallback(
    (tagOption: TagOption) => {
      if (isCreateNewTag(tagOption)) {
        // Don't create or add a new tag if at limit
        if (tagIds.length >= 2) {
          return;
        }
        const id = onCreateTag(tagOption.value);
        onToggleTag(id);
      } else {
        const id = tagOption.id;
        // If selecting an unassigned existing tag and at limit, block
        if (!tagIds.includes(id) && tagIds.length >= 2) {
          return;
        }
        onToggleTag(id);
      }
      setInputValue('');
      focusInput();
      setFocusedIndex(-1);
      setFocusedInlineIndex(tagIds.length + 1);
    },
    [onCreateTag, onToggleTag, focusInput, tagIds.length, tagIds]
  );

  const onInputKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        const selectedTag = tagOptions[safeFocusedIndex];
        if (safeFocusedIndex >= 0 && selectedTag) {
          onSelectTagOption(selectedTag);
        }
      } else if (e.key === 'Backspace' && inputValue === '' && tagIds.length) {
        const tagToRemove =
          safeInlineFocusedIndex < 0 || safeInlineFocusedIndex >= tagIds.length
            ? tagIds.length - 1
            : safeInlineFocusedIndex;
        tags.find((item: Tag) => item.id === tagIds.at(tagToRemove))?.untag(pageId);
      } else if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
        e.preventDefault();
        const newFocusedIndex = clamp(
          safeFocusedIndex + (e.key === 'ArrowUp' ? -1 : 1),
          0,
          tagOptions.length - 1
        );
        scrollContainerRef.current
          ?.querySelector(
            `.${styles.tagSelectorItem}:nth-child(${newFocusedIndex + 1})`
          )
          ?.scrollIntoView({ block: 'nearest' });
        setFocusedIndex(newFocusedIndex);
        // reset inline focus
        setFocusedInlineIndex(tagIds.length + 1);
      } else if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
        const newItemToFocus =
          e.key === 'ArrowLeft'
            ? safeInlineFocusedIndex - 1
            : safeInlineFocusedIndex + 1;

        e.preventDefault();
        setFocusedInlineIndex(newItemToFocus);
        // reset tag list focus
        setFocusedIndex(-1);
      }
    },
    [
      inputValue,
      tagIds,
      safeFocusedIndex,
      onSelectTagOption,
      tagOptions,
      safeInlineFocusedIndex,
      tags,
      pageId,
    ]
  );

  return (
    <div data-testid="tags-editor-popup" className={styles.tagsEditorRoot}>
      <div className={styles.tagsEditorSelectedTags}>
        <InlineTagsList
          pageId={pageId}
          readonly={readonly}
          focusedIndex={safeInlineFocusedIndex}
          onRemove={focusInput}
        >
          <input
            ref={inputRef}
            value={inputValue}
            onChange={onInputChange}
            onKeyDown={onInputKeyDown}
            autoFocus
            className={styles.searchInput}
            placeholder="Type here ..."
          />
        </InlineTagsList>
      </div>
      <div className={styles.tagsEditorTagsSelector}>
        <div className={styles.tagsEditorTagsSelectorHeader}>
          {t['com.pique.page-properties.tags.selector-header-title']()}
        </div>
        <Scrollable.Root>
          <Scrollable.Viewport
            ref={scrollContainerRef}
            className={styles.tagSelectorTagsScrollContainer}
          >
            {tagOptions.map((tag, idx) => {
              const commonProps = {
                focused: safeFocusedIndex === idx,
                onClick: () => onSelectTagOption(tag),
                onMouseEnter: () => setFocusedIndex(idx),
                ['data-testid']: 'tag-selector-item',
                ['data-focused']: safeFocusedIndex === idx,
                className: styles.tagSelectorItem,
              };
              if (isCreateNewTag(tag)) {
                return (
                  <div key={tag.value + '.' + idx} {...commonProps}>
                    {t['Create']()}{' '}
                    <TempTagItem value={inputValue} color={nextColor} />
                  </div>
                );
              } else {
                return (
                  <div
                    key={tag.id}
                    {...commonProps}
                    data-tag-id={tag.id}
                    data-tag-value={tag.value$.value}
                  >
                    <TagItem maxWidth="100%" tag={tag} mode="inline" />
                    <div className={styles.spacer} />
                    <EditTagMenu tagId={tag.id} onTagDelete={onTagDelete}>
                      <IconButton className={styles.tagEditIcon}>
                        <MoreHorizontalIcon />
                      </IconButton>
                    </EditTagMenu>
                  </div>
                );
              }
            })}
          </Scrollable.Viewport>
          <Scrollable.Scrollbar style={{ transform: 'translateX(6px)' }} />
        </Scrollable.Root>
      </div>
      <DeleteTagConfirmModal
        open={open}
        onOpenChange={handleCloseModal}
        selectedTagIds={selectedTagIds}
      />
    </div>
  );
};

interface TagsInlineEditorProps extends TagsEditorProps {
  placeholder?: string;
  className?: string;
}

// this tags value renderer right now only renders the legacy tags for now
export const TagsInlineEditor = ({
  pageId,
  readonly,
  placeholder,
  className,
}: TagsInlineEditorProps) => {
  const tagList = useService(TagService).tagList;
  const tagIds = useLiveData(tagList.tagIdsByPageId$(pageId));
  const empty = !tagIds || tagIds.length === 0;
  return (
    <Menu
      contentOptions={{
        side: 'bottom',
        align: 'start',
        sideOffset: 0,
        avoidCollisions: false,
        className: styles.tagsMenu,
        onClick(e: React.MouseEvent) {
          e.stopPropagation();
        },
      }}
      items={<TagsEditor pageId={pageId} readonly={readonly} />}
    >
      <div
        className={clsx(styles.tagsInlineEditor, className)}
        data-empty={empty}
        data-readonly={readonly}
      >
        {empty ? placeholder : <InlineTagsList pageId={pageId} readonly />}
      </div>
    </Menu>
  );
};
